import { AlertTriangle, Ban, Calendar, Hourglass, Layers } from "lucide-react";

import { Badge } from "../../components/Badge/Badge";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
} from "../../components/Card/Card";
import type { ContractShim, ContractStats } from "./api";
import {
  belowBreakEven,
  CONTRACT_WINDOW_DAYS,
  formatCount,
  formatObservedSince,
  formatShare,
  newestFirst,
  revisionRowsNewestFirst,
  revisionBadge,
  revisionName,
  revisionShare,
  revisionTone,
  revisionVersions,
  revisionWhat,
  shimShare,
  shimVerdict,
  totalPlayers,
  useContractStats,
} from "./clientContract";
import styles from "./ClientContractCards.module.css";

/**
 * The two cards at the bottom of Overview, and the reason the compatibility
 * mechanism has an end: the number a shim is deleted on.
 *
 * They are two components rather than one because they sit in different columns
 * of the page's 2fr/1fr grid — the distribution wants the room, the verdicts are
 * a list of names. One query serves both (useContractStats), so they cannot
 * disagree about who the population is.
 */

/** The badge tone each revision is drawn with; the bar uses the same four. */
const BADGE_VARIANT = {
  current: "accent",
  legacy: "neutral",
  zero: "warning",
  ahead: "info",
} as const;

const SEG_CLASS = {
  current: styles.segCurrent,
  legacy: styles.segLegacy,
  zero: styles.segZero,
  ahead: styles.segAhead,
} as const;

/**
 * Client versions — which revisions of the contract a project's players are
 * actually on, over thirty days.
 *
 * The window is the card's own and is drawn on it. The page above says «last 7
 * days»; a player who opens the game every other week is invisible in seven,
 * and a shim would be declared dead over people who were merely asleep. This is
 * the only place in the panel where a card's period differs from the page's,
 * which is exactly why it is stated here rather than left to be assumed.
 */
export function ClientVersionsCard({ projectId }: { projectId: string }) {
  const q = useContractStats(projectId);
  // A failed refetch leaves the last good answer in the cache, and this is not
  // a card that may draw one under an error notice: what is below carries a
  // verdict, and a verdict beside «couldn't load» is the exact confusion the
  // card exists to prevent. Four states, and no two of them at once.
  const data = q.isError ? undefined : q.data;

  return (
    <Card>
      <CardHeader>
        <h3>Client versions</h3>
        <span className={styles.rangeChip}>
          <Calendar size={13} aria-hidden="true" />
          <span>{data ? data.window_days : CONTRACT_WINDOW_DAYS} days</span>
        </span>
      </CardHeader>
      <CardBody>
        {q.isError ? (
          <ContractError
            what="client versions"
            alert
            onRetry={() => void q.refetch()}
          />
        ) : !data ? (
          <div className={styles.dim}>Loading revisions…</div>
        ) : !data.available ? (
          <ContractSilent />
        ) : (
          <>
            {!data.window_covered && <ShortHistory data={data} />}
            <Distribution data={data} />
          </>
        )}
      </CardBody>
      <CardFooter className={styles.note}>
        Distinct players who called in over the period. Revision 0 is both the
        builds that predate this mechanism and any request that carries no{" "}
        <code>X-SocialHub-Contract</code> header.
      </CardFooter>
    </Card>
  );
}

/**
 * The aggregate has not been watching for as long as the card is reporting on.
 *
 * A project on its first week and a Redis that lost its data both land here,
 * and both say the same true thing: the zeros below are an absence of history,
 * not an absence of players. It is stated above the numbers rather than under
 * them because it qualifies every one of them.
 */
function ShortHistory({ data }: { data: ContractStats }) {
  return (
    <div className={styles.short}>
      <span className={styles.shortIcon}>
        <Hourglass size={14} aria-hidden="true" />
      </span>
      <span>
        {data.observed_since ? (
          <>
            Counting since{" "}
            <b>{formatObservedSince(data.observed_since)}</b> — less than the{" "}
            {data.window_days}-day window, so a zero below is not yet an answer
            and no shim may be retired on one.
          </>
        ) : (
          <>
            Nothing has been counted for this project yet. The numbers start
            with its first request, and no shim may be retired until{" "}
            {data.window_days} days of them have gone by.
          </>
        )}
      </span>
    </div>
  );
}

/** The bar, its legend and one row per revision. */
function Distribution({ data }: { data: ContractStats }) {
  // The denominator is everybody, in wire order; what is drawn is newest first.
  const total = totalPlayers(data.revisions);
  const revisions = revisionRowsNewestFirst(data.revisions);

  return (
    <>
      {/* The legend below names every segment and its share in the same words,
          so the bar itself is decoration to anybody not looking at it. */}
      <div className={styles.stack} aria-hidden="true">
        {revisions.map((r) => (
          <span
            key={r.label}
            className={SEG_CLASS[revisionTone(r)]}
            style={{ width: `${revisionShare(r, total) * 100}%` }}
          />
        ))}
      </div>
      <div className={styles.legend}>
        {revisions.map((r) => (
          <span key={r.label} className={styles.legendItem}>
            <span className={`${styles.swatch} ${SEG_CLASS[revisionTone(r)]}`} />
            {revisionName(r)} — {formatShare(revisionShare(r, total))}
          </span>
        ))}
      </div>

      <div className={styles.rows}>
        {revisions.map((r) => {
          const versions = revisionVersions(r, data);
          return (
            // Dimmed at zero rather than dropped: the empty row is the answer
            // the card exists to give.
            <div
              key={r.label}
              className={`${styles.revRow} ${r.players === 0 ? styles.dead : ""}`}
            >
              <div>
                <Badge variant={BADGE_VARIANT[revisionTone(r)]}>
                  {revisionBadge(r)}
                </Badge>
              </div>
              <div className={styles.who}>
                <div className={styles.what}>{revisionWhat(r)}</div>
                <div className={styles.versions}>
                  {versions.quiet ? (
                    <span className={styles.quiet}>{versions.text}</span>
                  ) : (
                    versions.text
                  )}
                </div>
              </div>
              <div className={styles.num}>
                <div className={styles.pct}>
                  {formatShare(revisionShare(r, total))}
                </div>
                <div className={styles.cnt}>{formatCount(r.players)}</div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/**
 * Compatibility shims — every entry of the registry, with the share of players
 * still being served through it and what that share earns.
 *
 * There is no delete button here, on purpose. Deleting a shim is a code change
 * and the deletion of its test, done as a task; this card answers «is it time?»
 * and nothing else.
 */
export function CompatibilityShimsCard({ projectId }: { projectId: string }) {
  const q = useContractStats(projectId);
  // Stale rows are dropped on an error for the reason ClientVersionsCard gives
  // — here it is the verdict itself that must not outlive the answer it came
  // from.
  const data = q.isError ? undefined : q.data;
  const shims = newestFirst(data?.shims ?? []);

  return (
    <Card>
      <CardHeader>
        <h3>Compatibility shims</h3>
        {/* No count until there is one to state: a «0» while the answer is in
            flight is an assertion about the registry, not a placeholder. */}
        {data && <Badge variant="neutral">{shims.length}</Badge>}
      </CardHeader>
      <CardBody>
        {q.isError ? (
          <ContractError what="compatibility shims" onRetry={() => void q.refetch()} />
        ) : !data ? (
          <div className={styles.dim}>Loading shims…</div>
        ) : shims.length === 0 ? (
          // Not a placeholder. An empty registry is the correct state of the
          // platform: no breaking change has been made, so no answer is being
          // rewritten for anybody.
          <div className={styles.empty}>
            <div className={styles.emptyChip} aria-hidden="true">
              <Layers size={22} />
            </div>
            <div className={styles.emptyTitle}>No shims</div>
            <div className={styles.emptyBody}>
              Nothing is being rewritten for older builds — the API has made no
              breaking change yet. An entry appears here the first time one is
              made, together with the share of players it is kept for.
            </div>
          </div>
        ) : (
          shims.map((shim) => (
            <ShimRow key={shim.name} shim={shim} stats={data} />
          ))
        )}
      </CardBody>
      <CardFooter className={styles.note}>
        Two numbers, and the verdict is the second one: a shim serves every
        project there is, so it goes only when nobody <i>anywhere</i> has needed
        it across the whole window. Deleting one is a task of its own.
      </CardFooter>
    </Card>
  );
}

/**
 * One entry of the registry: what it is, who is still behind it here, who is
 * still behind it anywhere, and what the second of those earns it.
 */
function ShimRow({ shim, stats }: { shim: ContractShim; stats: ContractStats }) {
  const platform = stats.platform;
  const verdict = shimVerdict(shim, platform);

  return (
    <div className={styles.shimRow}>
      <div className={styles.sinfo}>
        <div className={styles.sname}>{shim.name}</div>
        <div className={styles.smeta}>
          Introduced in revision {shim.revision} · serves{" "}
          <b>rev &lt; {shim.revision}</b>
        </div>
        <div className={styles.smeta}>
          This project:{" "}
          {stats.available && shim.players !== null ? (
            <b>{formatCount(shim.players)} players</b>
          ) : (
            <span className={styles.quiet}>no numbers</span>
          )}
        </div>
        <div className={styles.smeta}>
          All projects:{" "}
          {verdict === "unknown" ? (
            <span className={styles.quiet}>
              no platform-wide numbers — that aggregate did not answer
            </span>
          ) : (
            <>
              <b>{formatCount(shim.platform_players ?? 0)} players</b>
              {verdict === "removable" && <> — nobody at all in {stats.window_days} days</>}
              {verdict === "tooSoon" && (
                <span className={styles.quiet}>
                  {platform.observed_since
                    ? ` — but counted only since ${formatObservedSince(platform.observed_since)}, too little history to retire it`
                    : " — but nothing has been counted yet, too little history to retire it"}
                </span>
              )}
              {verdict === "needed" && (
                <>
                  {" · "}
                  <b>{formatShare(shimShare(shim, platform))}</b>
                  {!platform.window_covered && (
                    <span className={styles.quiet}>
                      {platform.observed_since
                        ? ` since ${formatObservedSince(platform.observed_since)}`
                        : " so far — nothing has been counted yet"}
                    </span>
                  )}
                  {belowBreakEven(shim, platform) && (
                    <>
                      {" · "}below the <b>{formatShare(shim.retire_below)}</b> it
                      stops paying at
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
      {verdict === "removable" && (
        <Badge variant="success" dot>
          safe to delete
        </Badge>
      )}
      {verdict === "needed" && (
        <Badge variant="warning" dot>
          still needed
        </Badge>
      )}
      {/* No badge at all while the numbers are missing or younger than the
          window: a verdict is an assertion, and there is nothing to assert. */}
    </div>
  );
}

/**
 * The request itself failed. The rest of the page is unaffected and says so.
 *
 * Both cards read one query, so both fail together — and each names itself, so
 * that the strip in the right-hand column is not a message about the left one.
 * Only the first of them is a live region: the same failure announced twice is
 * a second failure to anybody listening rather than reading.
 */
function ContractError({
  what,
  onRetry,
  alert,
}: {
  what: string;
  onRetry: () => void;
  alert?: boolean;
}) {
  return (
    <div className={styles.errorStrip} role={alert ? "alert" : undefined}>
      <AlertTriangle size={14} aria-hidden="true" />
      <span>Couldn&apos;t load {what}.</span>
      <button type="button" className={styles.retry} onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}

/**
 * The request succeeded and the aggregate did not. A different silence, and it
 * gets different words: there is no distribution to draw and — the point of
 * saying so at all — no verdict to read off it.
 */
function ContractSilent() {
  return (
    <div className={`${styles.empty} ${styles.silent}`}>
      <span className={styles.silentIcon}>
        <Ban size={16} aria-hidden="true" />
      </span>
      <div>
        <div className={styles.emptyTitle}>No data</div>
        <div className={styles.emptyBody}>
          The aggregate did not answer, so nobody could be counted. No shim may
          be retired on this — an unanswered store is not a zero.
        </div>
      </div>
    </div>
  );
}
